import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Navbar from "../../components/Navbar";
import "aos/dist/aos.css";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import MA_Section_Homepage from "../../assets/images/MA_Section_Homepage.svg";
import icon from "../../assets/images/Icon.png";
import Icon_2 from "../../assets/images/Icon_2.png";
import Icon_3 from "../../assets/images/Icon_3.png";
import Icon_4 from "../../assets/images/Icon_4.png";
import rnd from "../../assets/videos/R&D_Section_Homepage.mp4";
import heroVideo from "../../assets/videos/hero-background.mp4";
import heroPoster from "../../assets/images/landing-poster.jpg";
import LandingHeroVideo from "../../components/LandingHeroVideo";
import DeferredSection from "../../components/DeferredSection";
import Testimonials from "../../components/Testimonials";
import ContactFooter from "../../components/ContactFooter";

const SolutionsList = [
  {
    id: 1,
    text: "R&D",
    title: "R&D",
    link: "rnd",
    video: rnd,
    description:
      "Predict patient outcomes, optimize clinical trials, and enhance recruitment efficiency for faster, more accurate therapeutic innovation",
  },
  {
    id: 2,
    text: "Medical Affairs",
    link: "medicalaffairs",
    img: MA_Section_Homepage,
    title: "Medical Affairs",
    description:
      "Measure variations in care, promote best practices, customize outreach, and measure the results of initiatives",
  },
];

const fallbackData = {
  heading: "Empowering organizations to improve patient care with AI and real-world data",
  subheading:
    "Clinica AI delivers predictive insights that support R&D teams, medical affairs, and care delivery partners.",
  primaryLinks: [
    { text: "Explore R&D Solutions", href: "/rnd" },
    { text: "Medical Affairs Insights", href: "/medicalAffairs" },
    { text: "Contact Clinica AI", href: "/contactus" },
  ],
};

export default function Landing() {
  const [currentToggle, setCurrentToggle] = useState(0);
  const [loadedVideos, setLoadedVideos] = useState([]);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: true,
      speed: 500,
      autoplay: false,
      autoplaySpeed: 3000,
      slidesToShow: 1,
      slidesToScroll: 1,
      afterChange: (index) => {
        setCurrentToggle(index);
      },
    }),
    [],
  );

  const handleClick = useCallback((index) => {
    sliderRef.current?.slickGoTo(index);
  }, []);

  const handleNavigate = useCallback((val) => {
    navigate(val);
  }, [navigate]);

  useEffect(() => {
    setLoadedVideos((prev) =>
      prev.includes(currentToggle) ? prev : [...prev, currentToggle],
    );
  }, [currentToggle]);

  return (
    <>
      <Navbar darkMode />
      
      {/* Screen reader accessible content */}
      <section className="sr-only">
        <h1>{fallbackData.heading}</h1>
        <p>{fallbackData.subheading}</p>
        <ul>
          {fallbackData.primaryLinks.map((link) => (
            <li key={link.text}>
              <a href={link.href}>{link.text}</a>
            </li>
          ))}
        </ul>
      </section>

      {/* Hero Section - Always has dark background for contrast */}
      <section className="w-full flex flex-col justify-center relative items-center h-screen"
        style={{ backgroundColor: "#0f172a", isolation: "isolate", minHeight: "100svh" }}>
        {/* Video Background */}
        <LandingHeroVideo src={heroVideo} poster={heroPoster} />

        {/* Gradient Overlay - ensures text is always readable */}
        <div 
          style={{ position: "absolute", inset: 0, pointerEvents: "none",
            background: "linear-gradient(to bottom, rgba(15,23,42,.75), rgba(15,23,42,.60), rgba(15,23,42,.75))" }}
          aria-hidden="true"
        />

        {/* Content */}
        <div className="z-10 rounded-xl md:px-12 px-6 py-10 flex flex-col items-center justify-center gap-6 max-w-4xl text-center">
          <h2 
            className="md:text-5xl text-2xl font-medium leading-tight"
            style={{ color: '#ffffff' }}
          >
            Empowering organizations to improve <br className="hidden md:block" /> patient care with AI and RWD
          </h2>
          <p 
            className="text-lg max-w-2xl"
            style={{ color: 'rgba(255, 255, 255, 0.85)' }}
          >
            Predictive insights that support R&D teams, medical affairs, and care delivery partners
          </p>
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => handleNavigate("rnd")}
              className="px-8 py-3 text-sm font-semibold bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              R&D
            </button>
            <button
              onClick={() => handleNavigate("medicalaffairs")}
              className="px-8 py-3 text-sm font-semibold bg-transparent text-white border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Medical Affairs
            </button>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative overflow-hidden py-16 bg-gray-50">
        <div className="flex flex-wrap justify-center gap-4 items-center px-10 py-4">
          {SolutionsList.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleClick(index)}
              className="px-6 py-2 font-medium text-lg rounded-lg transition-all border"
              style={currentToggle === index 
                ? { backgroundColor: '#0f172a', color: '#ffffff', borderColor: '#0f172a' }
                : { backgroundColor: '#ffffff', color: '#374151', borderColor: '#e5e7eb' }
              }
            >
              {item.text}
            </button>
          ))}
        </div>
        <div className="md:px-20 px-6 h-full justify-center flex flex-col relative mt-8">
          <Slider ref={sliderRef} {...settings}>
            {SolutionsList.map((item, index) => {
              const isActive = currentToggle === index;
              const isLoaded = loadedVideos.includes(index);
              const shouldRenderVideo = isActive || isLoaded;
              
              return (
                <div key={item.id} className="md:px-4 px-0 w-full">
                  <div className="flex flex-col md:flex-row items-center gap-8 md:justify-between">
                    <div className="flex h-full justify-center flex-col items-center gap-4 flex-1">
                      <h3 
                        className="md:text-4xl text-2xl font-semibold text-center"
                        style={{ color: '#0f172a' }}
                      >
                        {item.title}
                      </h3>
                      <p 
                        className="md:text-lg text-base max-w-lg text-center"
                        style={{ color: '#475569' }}
                      >
                        {item.description}
                      </p>
                      <button
                        onClick={() => handleNavigate(item.link)}
                        className="px-8 py-3 rounded-lg transition-colors mt-4 font-medium"
                        style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
                      >
                        Learn more
                      </button>
                    </div>

                    {item.img && (
                      <div className="flex-1 flex justify-center mt-8 md:mt-0">
                        <img
                          src={item.img}
                          alt={item.title}
                          className="max-h-80 object-contain"
                        />
                      </div>
                    )}
                    {item.video && (
                      <div className="flex-1 flex justify-center mt-8 md:mt-0">
                        <div 
                          className="relative overflow-hidden rounded-lg shadow-lg"
                          style={{ width: '100%', maxWidth: '560px', height: '320px' }}
                        >
                          {shouldRenderVideo ? (
                            <DeferredSection label="R&D preview" minHeight={320}><video
                              className="object-cover w-full h-full"
                              autoPlay
                              muted
                              loop
                              playsInline
                              preload="metadata"
                            >
                              <source src={item.video} type="video/mp4" />
                            </video></DeferredSection>
                          ) : (
                            <div className="w-full h-full bg-gray-200 animate-pulse" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </section>

      <Testimonials />

      {/* Features Section */}
      <section style={{ backgroundColor: '#f9fafb' }}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 grid-cols-1 px-6 md:px-12 py-20 gap-12">
          <div className="flex flex-col items-start gap-4">
            <h2 
              className="md:text-4xl text-2xl font-semibold"
              style={{ color: '#0f172a' }}
            >
              The most predictive machine learning algorithms
            </h2>
            <p style={{ color: '#475569', fontSize: '1.125rem' }}>
              Leveraging a proprietary library of more than 50,000 patient features
            </p>
          </div>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
            <div className="flex items-center gap-4 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#ffffff' }}>
              <img src={icon} alt="Predictive features" className="w-12 h-12" />
              <h3 className="text-lg font-semibold" style={{ color: '#0f172a' }}>{">50k predictive features"}</h3>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#ffffff' }}>
              <img src={Icon_2} alt="All diseases" className="w-12 h-12" />
              <h3 className="text-lg font-semibold" style={{ color: '#0f172a' }}>All diseases</h3>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#ffffff' }}>
              <img src={Icon_3} alt="Use cases" className="w-12 h-12" />
              <h3 className="text-lg font-semibold" style={{ color: '#0f172a' }}>Dozens of use cases</h3>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-lg shadow-sm" style={{ backgroundColor: '#ffffff' }}>
              <img src={Icon_4} alt="Data sources" className="w-12 h-12" />
              <h3 className="text-lg font-semibold" style={{ color: '#0f172a' }}>Claims, Medical Records, Proprietary Datasets</h3>
            </div>
          </div>
        </div>
      </section>

      <ContactFooter />
      <Footer />
    </>
  );
}
